import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { AiToolCall } from '@/types/ai';

interface FollowUpChipsProps {
  toolCalls?: AiToolCall[] | null;
  content: string;
  onSelect: (prompt: string) => void;
  className?: string;
}

interface FollowUpSuggestion {
  label: string;
  prompt: string;
}

const TOOL_FOLLOW_UPS: Record<string, FollowUpSuggestion[]> = {
  get_daily_sales: [
    { label: 'Comparer avec hier', prompt: 'Compare mes ventes d\'aujourd\'hui avec hier.' },
    { label: 'Détail par produit', prompt: 'Quels produits se sont le mieux vendus aujourd\'hui ?' },
    { label: 'Rapport PDF', prompt: 'Génère un rapport PDF des ventes du jour.' },
  ],
  get_monthly_revenue: [
    { label: 'Tendance sur 3 mois', prompt: 'Quelle est la tendance de mon CA sur les 3 derniers mois ?' },
    { label: 'Top produits du mois', prompt: 'Quels sont mes meilleurs produits ce mois-ci ?' },
  ],
  get_stock_alerts: [
    { label: 'Commander les manquants', prompt: 'Quels produits dois-je commander en priorité ?' },
    { label: 'Produits lents', prompt: 'Quels produits se vendent le moins bien ?' },
  ],
  get_top_products: [
    { label: 'Produits à pousser', prompt: 'Quels produits devrais-je mettre en avant ?' },
    { label: 'Marge par produit', prompt: 'Quelle est la marge sur mes produits les plus vendus ?' },
  ],
  search_customer: [
    { label: 'Historique d\'achats', prompt: 'Montre-moi l\'historique d\'achats de ce client.' },
    { label: 'Relancer ce client', prompt: 'Rédige un message pour relancer ce client.' },
  ],
  get_customer_history: [
    { label: 'Produits recommandés', prompt: 'Quels produits recommander à ce client ?' },
    { label: 'Message de fidélisation', prompt: 'Rédige un message de fidélisation pour ce client.' },
  ],
  get_shop_health: [
    { label: 'Plan d\'amélioration', prompt: 'Propose un plan d\'action pour améliorer mon score.' },
    { label: 'Comparer avec la semaine', prompt: 'Comment a évolué la santé de ma boutique cette semaine ?' },
  ],
  get_profit_analysis: [
    { label: 'Optimiser les marges', prompt: 'Comment puis-je améliorer mes marges ?' },
    { label: 'Produits à reprendre', prompt: 'Quels produits non-rentables devrais-je arrêter ?' },
  ],
  generate_pdf_report: [
    { label: 'Rapport Excel', prompt: 'Génère ce même rapport en format Excel.' },
  ],
  generate_excel_spreadsheet: [
    { label: 'Rapport PDF', prompt: 'Génère ce même rapport en format PDF.' },
  ],
  preview_sale: [
    { label: 'Confirmer la vente', prompt: 'Confirmer' },
  ],
  get_daily_summary: [
    { label: 'Actions prioritaires', prompt: 'Quelles sont les 3 actions prioritaires pour demain ?' },
    { label: 'Rapport de la journée', prompt: 'Génère un rapport PDF de la journée.' },
  ],
  get_employee_performance: [
    { label: 'Classement détaillé', prompt: 'Montre le classement détaillé par employé avec le CA.' },
  ],
  get_reorder_suggestions: [
    { label: 'Budget nécessaire', prompt: 'Quel budget je dois prévoir pour ces commandes ?' },
  ],
};

const CONTENT_FOLLOW_UPS: Array<{ pattern: RegExp; suggestions: FollowUpSuggestion[] }> = [
  {
    pattern: /vente|chiffre|revenue|CA/i,
    suggestions: [
      { label: 'Comparer les périodes', prompt: 'Compare mes ventes de cette semaine avec la précédente.' },
    ],
  },
  {
    pattern: /stock|rupture|inventaire/i,
    suggestions: [
      { label: 'Alertes stock', prompt: 'Quels produits sont en rupture de stock ?' },
    ],
  },
  {
    pattern: /client|customer/i,
    suggestions: [
      { label: 'Clients inactifs', prompt: 'Quels clients n\'ont pas acheté depuis 30 jours ?' },
    ],
  },
];

function getSuggestions(toolCalls?: AiToolCall[] | null, content?: string): FollowUpSuggestion[] {
  const suggestions: FollowUpSuggestion[] = [];
  const seen = new Set<string>();

  if (toolCalls && toolCalls.length > 0) {
    for (const call of toolCalls) {
      const followUps = TOOL_FOLLOW_UPS[call.tool];
      if (followUps) {
        for (const s of followUps) {
          if (!seen.has(s.label)) {
            seen.add(s.label);
            suggestions.push(s);
          }
        }
      }
    }
  }

  if (suggestions.length < 2 && content) {
    for (const rule of CONTENT_FOLLOW_UPS) {
      if (rule.pattern.test(content)) {
        for (const s of rule.suggestions) {
          if (!seen.has(s.label)) {
            seen.add(s.label);
            suggestions.push(s);
          }
        }
      }
    }
  }

  return suggestions.slice(0, 3);
}

export function FollowUpChips({ toolCalls, content, onSelect, className }: FollowUpChipsProps) {
  const suggestions = getSuggestions(toolCalls, content);

  if (suggestions.length === 0) return null;

  return (
    <div className={cn('mt-2 flex flex-wrap gap-1.5', className)}>
      {suggestions.map((s, i) => (
        <motion.button
          key={s.label}
          type="button"
          onClick={() => onSelect(s.prompt)}
          className="rounded-lg border border-charcoal-200 dark:border-charcoal-700 bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-charcoal-700 dark:text-charcoal-300 hover:bg-muted active:scale-[0.97] transition-all"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.06, duration: 0.15 }}
        >
          {s.label}
        </motion.button>
      ))}
    </div>
  );
}
